import type {
  PullRequestDetail,
  PullRequestDiscussionInput,
  PullRequestEditResult,
  PullRequestLifecycleAction,
} from "@afar-research/contracts";
import { lifecycleAllowed } from "@afar-research/shared/pullRequestLifecycle";
import { useStore } from "zustand";
import { runPullRequestCommand } from "../pullRequestCommand";
import { invalidatePullRequest } from "../pullRequestInvalidation";
import type { PullRequestSurfaceDescriptor } from "../pullRequestSurfaceStore";
import type { PullRequestLifecycleSession } from "../pullRequestLifecycle";
import { newCommandId } from "../lib/utils";
import { MoreHorizontalIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { orderMergeMethodsByPreference } from "./pullRequest/preferredMergeMethod.logic";
import { resolveProjectSettings } from "@afar-research/afar";
import { useEnvironmentSettings } from "../hooks/useSettings";

export function PullRequestLifecycleForm({
  descriptor,
  detail,
  session,
  enabled,
  mergeEnabled = false,
  execute,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly detail: PullRequestDetail;
  readonly session: PullRequestLifecycleSession;
  readonly enabled: boolean;
  readonly mergeEnabled?: boolean;
  readonly execute: (input: PullRequestDiscussionInput) => Promise<PullRequestEditResult>;
}) {
  const { command, pending, result } = useStore(session);
  // This surface lives outside the settings scope, so it reads the
  // Environment's own settings and lays this project's override on top.
  const preferredMergeMethod = useEnvironmentSettings(
    descriptor.environmentId,
    (settings) => resolveProjectSettings(settings, detail.projectId).pullRequestMergeMethod,
  );
  const unresolved = command !== null && result?.status !== "settled";
  const run = (input: PullRequestDiscussionInput) =>
    runPullRequestCommand({
      session,
      input,
      enabled,
      execute,
      onAccepted: () => invalidatePullRequest(input),
    });
  const submit = (action: PullRequestLifecycleAction) => {
    if (
      !enabled ||
      unresolved ||
      pending ||
      !detail.projectRevision ||
      !lifecycleAllowed(detail, action)
    )
      return;
    void run({
      environmentId: descriptor.environmentId,
      projectId: detail.projectId,
      repository: detail.repository,
      number: detail.number,
      expectedRevision: detail.projectRevision,
      resourceVersion: detail.resourceVersion,
      commandId: newCommandId(),
      action,
    });
  };
  const disabled = !enabled || !detail.projectRevision;
  const operations =
    detail.state === "merged"
      ? []
      : detail.state === "closed"
        ? (["reopen"] as const)
        : ([detail.isDraft ? "ready" : "draft", "close"] as const);

  const OPERATION_LABELS = {
    ready: "Mark ready for review",
    draft: "Convert to draft",
    close: "Close Pull Request",
    reopen: "Reopen Pull Request",
  } as const;

  /** Every lifecycle choice this viewer may actually make, in the order it is offered. */
  const availableActions: ReadonlyArray<{
    readonly label: string;
    readonly action: PullRequestLifecycleAction;
  }> = [
    ...(mergeEnabled && detail.headRefOid
      ? (["merge", "enable-auto-merge"] as const).flatMap((kind) =>
          orderMergeMethodsByPreference(
            detail.capabilities.mergeMethods,
            preferredMergeMethod,
          ).flatMap((method) => {
            const action: PullRequestLifecycleAction = {
              kind,
              method,
              expectedHeadOid: detail.headRefOid!,
            };
            return lifecycleAllowed(detail, action) &&
              (kind !== "enable-auto-merge" || (detail.autoMergeAllowed && !detail.autoMergeMethod))
              ? [
                  {
                    label: `${kind === "merge" ? "Merge with " : "Enable auto-merge with "}${method}`,
                    action,
                  },
                ]
              : [];
          }),
        )
      : []),
    ...(mergeEnabled &&
    detail.autoMergeMethod &&
    lifecycleAllowed(detail, { kind: "disable-auto-merge" })
      ? [
          {
            label: "Disable auto-merge",
            action: { kind: "disable-auto-merge" } as PullRequestLifecycleAction,
          },
        ]
      : []),
    ...operations.flatMap((operation) =>
      lifecycleAllowed(detail, { kind: "lifecycle", operation })
        ? [
            {
              label: OPERATION_LABELS[operation],
              action: { kind: "lifecycle", operation } as PullRequestLifecycleAction,
            },
          ]
        : [],
    ),
    ...(detail.state === "open" && detail.headRefOid && detail.baseRefOid
      ? (["merge", "rebase"] as const).flatMap((method) => {
          const action: PullRequestLifecycleAction = {
            kind: "update-branch",
            method,
            expectedHeadOid: detail.headRefOid!,
            expectedBaseOid: detail.baseRefOid!,
          };
          return lifecycleAllowed(detail, action)
            ? [{ label: `Update branch with ${method}`, action }]
            : [];
        })
      : []),
  ];
  const [primaryAction = null, ...secondaryActions] = availableActions;
  return (
    // Following T3Code, the lifecycle is the panel's own action row rather than a titled
    // section: the one action this pull request is actually waiting for reads as a button, and
    // everything else — including the policy this repository enforces — sits a press away.
    <section aria-label="Pull Request lifecycle" className="grid gap-2">
      {result && (
        <p role="status">
          {result.status === "settled"
            ? result.receipt.status === "accepted"
              ? "Pull Request updated."
              : result.receipt.result.message
            : result.message}
        </p>
      )}
      {pending ? (
        <p role="status">Updating Pull Request…</p>
      ) : unresolved ? (
        <div className="grid gap-2">
          <Button disabled={!enabled} onClick={() => void run(command)}>
            Check outcome
          </Button>
          {mergeEnabled &&
            result?.status === "uncertain" &&
            (command.action.kind === "merge" ||
              command.action.kind === "enable-auto-merge" ||
              command.action.kind === "disable-auto-merge") && (
              <>
                <p className="text-sm">
                  GitHub could not prove this command's outcome. Inspect the Pull Request on GitHub
                  before dismissing. Dismissing does not mark the command successful or repeat it.
                </p>
                <a href={descriptor.url ?? detail.url} target="_blank" rel="noreferrer">
                  Inspect on GitHub
                </a>
                <Button
                  disabled={!enabled}
                  onClick={() => {
                    session.setState({ command: null, result: null });
                    invalidatePullRequest(command);
                  }}
                >
                  I checked GitHub; dismiss uncertainty
                </Button>
              </>
            )}
        </div>
      ) : (
        <>
          {/* One context-sensitive action is offered outright and every other choice sits
              behind the overflow, following T3Code: the row of ten equal buttons this replaced
              gave merging a pull request the same weight as converting it to a draft. Nothing
              is removed - each action still passes the same permission and freshness gates. */}
          <div className="flex flex-wrap items-center gap-2" aria-label="Lifecycle actions">
            {primaryAction === null ? null : (
              <Button disabled={disabled} onClick={() => submit(primaryAction.action)}>
                {primaryAction.label}
              </Button>
            )}
            {secondaryActions.length > 0 ? (
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      aria-label="More pull request actions"
                      disabled={disabled}
                      size="icon-xs"
                      variant="ghost"
                    />
                  }
                >
                  <MoreHorizontalIcon className="size-3.5" />
                </MenuTrigger>
                <MenuPopup align="end" side="bottom">
                  {secondaryActions.map((entry) => (
                    <MenuItem key={entry.label} onClick={() => submit(entry.action)}>
                      {entry.label}
                    </MenuItem>
                  ))}
                </MenuPopup>
              </Menu>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
